import { DepartureArrivalDateTimeDTO, EventDTO, FromToDTO, ParcelResponseDTO, RouteDTO } from "./response.types";
import { ParcelMessagesDTO } from "entities/shared/parcelCard.types";
import { ParcelStatusesDTO } from "entities/shared/parcelCard.types";
import { CoordinateModelDTO, DateTimeModel, SearchReferenceDTO } from "entities/shared/shipmentCard.types";
import { ShipmentCardMetadataDTO } from "entities/shared/subscriptionModel";
import { Nullable, ValueOf } from "shared/lib/typeCheck.lib";

export class SearchApiParser {
  private model: ParcelResponseDTO;
  public readonly requestLimitReached: boolean = false;
  public readonly isSuccess: boolean = false;

  constructor(model: ParcelResponseDTO) {
    this.model = model;
    this.requestLimitReached = this.model.status_code === ParcelMessagesDTO.API_KEY_ACCESS_DENIED;
    this.isSuccess = this.model.status_code === ParcelMessagesDTO.OK;
  }

  get metadata(): ShipmentCardMetadataDTO {
    return this.model.metadata;
  }

  get responseStatus(): ValueOf<typeof ParcelMessagesDTO> {
    return this.model.status_code;
  }

  get number(): SearchReferenceDTO {
    return this.model.metadata.request_parameters.number;
  }

  get carrierCode(): string {
    return this.model.metadata.parcel_company.carrier_code;
  }

  get status(): ValueOf<typeof ParcelStatusesDTO> {
    return this.model.data.status ?? ParcelStatusesDTO.UNKNOWN;
  }

  get carrierName(): string {
    return this.model.metadata.parcel_company.name;
  }

  get fromLocation(): Nullable<FromToDTO> {
    return this.model.data.from ?? null;
  }

  get toLocation(): Nullable<FromToDTO> {
    return this.model.data.to ?? null;
  }

  get departureDatetime(): Nullable<DepartureArrivalDateTimeDTO> {
    return this.model.data?.departure_datetime ?? null;
  }

  get arrivalDatetime(): Nullable<DepartureArrivalDateTimeDTO> {
    return this.model.data?.arrival_datetime ?? null;
  }

  get polCoordinate(): CoordinateModelDTO | null {
    const directionFrom = this.model.data.route?.[0]?.from;
    if (!directionFrom?.lat || !directionFrom?.lng) return null;
    return {
      lat: directionFrom.lat,
      lng: directionFrom.lng,
    };
  }

  get podCoordinate(): CoordinateModelDTO | null {
    const directionTo = this.model.data?.route?.[this.model.data.route?.length - 1]?.to;
    if (!directionTo?.lat || !directionTo?.lng) return null;
    return {
      lat: directionTo.lat,
      lng: directionTo.lng,
    };
  }

  get routes(): RouteDTO[] {
    return this.model.data?.route ?? [];
  }

  get events(): EventDTO[] {
    return this.model.data.events ?? [];
  }

  get etd(): Nullable<DateTimeModel> {
    return this.model.data?.departure_datetime?.estimated ?? null;
  }

  get atd(): Nullable<DateTimeModel> {
    return this.model.data?.departure_datetime?.actual ?? null;
  }

  get eta(): Nullable<DateTimeModel> {
    return this.model.data?.arrival_datetime?.estimated ?? null;
  }

  get ata(): Nullable<DateTimeModel> {
    return this.model.data?.arrival_datetime?.actual ?? null;
  }
}
